/**
 * ManualSearch — moteur de recherche full-text du manuel pédagogique.
 *
 * Parcourt tous les chapitres → pages → champs textuels (term/def/title/
 * intro/bullets/items/situation/solution/lesson/mistake/signal/fix/example/
 * formula) et retourne une liste plate de résultats avec :
 *   - chapitre d'origine (couleur + titre)
 *   - page (titre + index absolu pour le flip)
 *   - snippet (texte autour du match)
 *
 * 04/02/2026 — créé pour la phase « Recherche + TOC enrichie » (P3 / UX).
 */
import React, { useMemo, useState, useCallback } from 'react';
import { Search, X, ArrowRight } from 'lucide-react';

/* ───────── Helpers ───────── */
function normalize(str) {
  return (str || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Build a snippet of `text` centered around the first occurrence of `query`.
 * Returns { text, indices } where indices marks the highlight range.
 */
function makeSnippet(text, queryLower, radius = 60) {
  if (!text) return null;
  const lower = normalize(text);
  const i = lower.indexOf(queryLower);
  if (i < 0) return null;
  const start = Math.max(0, i - radius);
  const end = Math.min(text.length, i + queryLower.length + radius);
  const prefix = start > 0 ? '…' : '';
  const suffix = end < text.length ? '…' : '';
  return {
    raw: text,
    snippet: prefix + text.slice(start, end) + suffix,
    matchStart: prefix.length + (i - start),
    matchLen: queryLower.length,
  };
}

/**
 * Extract every searchable string from a page (depends on its `type`).
 * Returns an array of { label, value } so we know WHICH field matched and
 * can display a contextual snippet.
 */
function extractPageFields(page) {
  const out = [];
  if (page.title) out.push({ label: 'title', value: page.title });
  if (page.intro) out.push({ label: 'intro', value: page.intro });
  (page.bullets || []).forEach((b) => out.push({ label: 'bullet', value: b }));
  (page.items || []).forEach((it) => {
    if (it.term) out.push({ label: 'term', value: it.term });
    if (it.word) out.push({ label: 'term', value: it.word });
    if (it.name) out.push({ label: 'term', value: it.name });
    if (it.title) out.push({ label: 'title', value: it.title });
    if (it.def) out.push({ label: 'def', value: it.def });
    if (it.formula) out.push({ label: 'formula', value: it.formula });
    if (it.example) out.push({ label: 'example', value: it.example });
    if (it.situation) out.push({ label: 'case', value: it.situation });
    if (it.solution) out.push({ label: 'case', value: it.solution });
    if (it.lesson) out.push({ label: 'case', value: it.lesson });
    if (it.mistake) out.push({ label: 'pitfall', value: it.mistake });
    if (it.signal) out.push({ label: 'pitfall', value: it.signal });
    if (it.fix) out.push({ label: 'pitfall', value: it.fix });
  });
  (page.legend || []).forEach((l) => {
    if (l.label) out.push({ label: 'legend', value: l.label });
    if (l.desc) out.push({ label: 'legend', value: l.desc });
  });
  return out;
}

/**
 * Walk all chapters → pages and build a flat index.
 * Each entry = { chapter, page, fields[], absoluteIndex }
 * Absolute index = position in the book including the front cover (+1).
 */
function buildIndex(chapters) {
  const index = [];
  let cursor = 1; // +1 because the book starts with the front cover
  chapters.forEach((chapter) => {
    chapter.pages.forEach((page) => {
      index.push({
        chapter,
        page,
        fields: extractPageFields(page),
        absoluteIndex: cursor,
      });
      cursor += 1;
    });
  });
  return index;
}

/**
 * Hit highlighting — split snippet into [before, match, after] for
 * a <mark>-style render. Pure presentational helper.
 */
function HighlightedSnippet({ snippet }) {
  if (!snippet) return null;
  const before = snippet.snippet.slice(0, snippet.matchStart);
  const match = snippet.snippet.slice(
    snippet.matchStart,
    snippet.matchStart + snippet.matchLen,
  );
  const after = snippet.snippet.slice(snippet.matchStart + snippet.matchLen);
  return (
    <span className="text-[11px] leading-snug text-slate-300">
      {before}
      <mark className="bg-amber-400/30 text-amber-100 rounded px-0.5">{match}</mark>
      {after}
    </span>
  );
}

const LABEL_TAG_COLORS = {
  title:   'bg-amber-600/20 text-amber-300 border-amber-500/40',
  intro:   'bg-amber-600/20 text-amber-300 border-amber-500/40',
  bullet:  'bg-slate-600/30 text-slate-300 border-slate-500/40',
  term:    'bg-emerald-600/20 text-emerald-300 border-emerald-500/40',
  def:     'bg-emerald-600/20 text-emerald-300 border-emerald-500/40',
  formula: 'bg-indigo-600/20 text-indigo-300 border-indigo-500/40',
  example: 'bg-indigo-600/20 text-indigo-300 border-indigo-500/40',
  case:    'bg-sky-600/20 text-sky-300 border-sky-500/40',
  pitfall: 'bg-rose-600/20 text-rose-300 border-rose-500/40',
  legend:  'bg-purple-600/20 text-purple-300 border-purple-500/40',
};

const LABEL_TEXT = {
  fr: {
    title: 'Titre', intro: 'Intro', bullet: 'Point clé', term: 'Terme',
    def: 'Définition', formula: 'Formule', example: 'Exemple', case: 'Cas',
    pitfall: 'Piège', legend: 'Légende',
  },
  en: {
    title: 'Title', intro: 'Intro', bullet: 'Bullet', term: 'Term',
    def: 'Definition', formula: 'Formula', example: 'Example', case: 'Case',
    pitfall: 'Pitfall', legend: 'Legend',
  },
};

/**
 * Main component.
 * Props:
 *   - chapters       : MANUAL_CHAPTERS for the active language
 *   - language       : 'fr' | 'en'
 *   - onResultClick  : (absolutePageIndex) => void — flips the book
 *   - className      : optional extra classes
 */
export default function ManualSearch({ chapters, language, onResultClick, className = '' }) {
  const [query, setQuery] = useState('');
  const index = useMemo(() => buildIndex(chapters), [chapters]);
  const t = LABEL_TEXT[language] || LABEL_TEXT.fr;

  const results = useMemo(() => {
    const q = normalize(query.trim());
    if (q.length < 2) return [];
    const out = [];
    for (const entry of index) {
      let firstHit = null;
      for (const f of entry.fields) {
        const snip = makeSnippet(f.value, q);
        if (snip) {
          firstHit = { label: f.label, snip };
          break;
        }
      }
      if (firstHit) {
        out.push({ ...entry, hit: firstHit });
        if (out.length >= 40) break; // hard cap to keep UI snappy
      }
    }
    return out;
  }, [query, index]);

  const handleClear = useCallback(() => setQuery(''), []);

  return (
    <div className={`space-y-2 ${className}`} data-testid="manual-search">
      {/* Input */}
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={language === 'en' ? 'Search the manual…' : 'Rechercher dans le manuel…'}
          className="w-full pl-8 pr-7 py-2 text-xs bg-ink-900 border border-ink-600 rounded-md text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30"
          data-testid="manual-search-input"
          aria-label={language === 'en' ? 'Search the manual' : 'Rechercher dans le manuel'}
        />
        {query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 transition-colors"
            data-testid="manual-search-clear"
            aria-label={language === 'en' ? 'Clear search' : 'Effacer'}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Results */}
      {query.trim().length >= 2 && (
        <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1" data-testid="manual-search-results">
          {results.length === 0 ? (
            <p className="text-[11px] text-slate-500 italic px-2 py-3 text-center">
              {language === 'en' ? 'No match found.' : 'Aucun résultat.'}
            </p>
          ) : (
            <>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono px-1 pb-1">
                {results.length}{results.length >= 40 ? '+' : ''}{' '}
                {language === 'en'
                  ? (results.length > 1 ? 'matches' : 'match')
                  : (results.length > 1 ? 'résultats' : 'résultat')}
              </p>
              {results.map((r, i) => {
                const labelClass = LABEL_TAG_COLORS[r.hit.label] || LABEL_TAG_COLORS.bullet;
                return (
                  <button
                    key={`${r.chapter.id}-${r.absoluteIndex}-${i}`}
                    type="button"
                    onClick={() => onResultClick(r.absoluteIndex)}
                    className="group w-full text-left px-2 py-2 rounded-md border border-ink-700 bg-ink-900/60 hover:bg-ink-700/60 hover:border-amber-500/40 transition-all"
                    data-testid={`manual-search-result-${i}`}
                    style={{ borderLeftColor: r.chapter.color, borderLeftWidth: 3 }}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 truncate">
                        {r.chapter.title}
                      </span>
                      <ArrowRight className="w-3 h-3 text-slate-600 group-hover:text-amber-400 transition-colors flex-shrink-0" />
                    </div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded border font-mono ${labelClass}`}>
                        {t[r.hit.label] || r.hit.label}
                      </span>
                      <span className="text-[11px] text-slate-200 font-medium truncate">
                        {r.page.title || (language === 'en' ? 'Page' : 'Page')}
                      </span>
                    </div>
                    <HighlightedSnippet snippet={r.hit.snip} />
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
